import React, { useState } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";

import UploadDocuments from "./pages/UploadDocuments";
import Login from "./pages/Login";
import PrivateRoute from "./components/PrivateRoute";
import RoleBasedRoute from "./components/RoleBasedRoute";

import Credentials from "./pages/Credentials";
import ExpiredCredentials from "./pages/ExpiredCredentials";
import MissingCredentials from "./pages/MissingCredentials";
import ViewDocuments from "./pages/ViewDocuments";
import TbTestForm from "./pages/TbTestForm";
import EmployeeHealthExamination from "./pages/EmployeeHealthExamination";
import FluVaccinationForm from "./pages/FluVaccinationForm";
import PatientConsentForm from "./pages/PatientConsentForm";
import MedicareNonCoverageForm from "./pages/MedicareNonCoverageForm";
import SubmittedDocuments from "./pages/SubmittedDocuments";
import ApplicationForm from "./pages/ApplicationForm";
import RnWelcomeLetter from "./pages/RnWelcomeLetter";
import Invoice from "./pages/Invoice";
import AddEmployeeModal from "./components/Employees/AddEmployeeModal";
import Payroll from "./pages/Payroll";
import EvaluationNew from "./pages/EvaluationNew";
import ChangePassword from "./pages/ChangePassword";
import Profile from "./pages/Profile";
import ApplicationsRedirect from "./components/ApplicationsRedirect";

import GeneralForms from "./pages/Application/GeneralForms/GeneralForms";
import RouteSheet from "./pages/Application/GeneralForms/RouteSheet";
import DailyLog from "./pages/Application/GeneralForms/DailyLog";
import PatientConsent from "./pages/Application/GeneralForms/PatientConsent";
import MedicareNonCoverage from "./pages/Application/GeneralForms/MedicareNonCoverage";
import GeneralFormsList from "./pages/Application/GeneralForms/GeneralFormsList";
import RequireVisibleGeneralForm from "./pages/Application/GeneralForms/RequireVisibleGeneralForm";
import SurveyorAssignment from "./pages/Surveyor/SurveyorAssignment";
import Documents from "./pages/Documents/Documents";
import Reports from "./pages/Reports";
import ManageSubscriptions from "./pages/ManageSubscriptions";
import CoverageArea from "./pages/CoverageArea";
import QaCenter from "./pages/QaCenter/QaCenter";
import BillingModule from "./pages/Billing/BillingModule";
import LibrariesModule from "./pages/Libraries/LibrariesModule";
import LibrariesSectionPage from "./pages/Libraries/LibrariesSectionPage";
import CarePlanLibraryHub from "./pages/Libraries/CarePlanLibrary/CarePlanLibraryHub";
import CarePlanSectionPage from "./pages/Libraries/CarePlanLibrary/CarePlanSectionPage";
import Patients from "./pages/Patients/Patients";
import PatientForm from "./pages/Patients/PatientForm";
import PatientDetail from "./pages/Patients/PatientDetail";
import PatientChartsPage from "./pages/Patients/PatientChartsPage";
import PatientChartFormPage from "./pages/Patients/PatientChartFormPage";
import PatientClinicalFormPage from "./pages/Patients/components/clinicalForms/PatientClinicalFormPage";
import PatientClinicalFormPrintPage from "./pages/Patients/components/clinicalForms/PatientClinicalFormPrintPage";
import PatientClinicalFormsBulkPrintPage from "./pages/Patients/components/clinicalForms/PatientClinicalFormsBulkPrintPage";
import DocumentsPrintPreviewPage from "./pages/Documents/DocumentsPrintPreviewPage";
import PatientDemographicsPrintPage from "./pages/Patients/components/PatientDemographicsPrintPage";
import PatientAssessmentPrintPage from "./pages/Patients/components/PatientAssessmentPrintPage";
import PatientStaffSchedulePrintPage from "./pages/Patients/PatientStaffSchedulePrintPage";
import Support from "./pages/Support";
import Training from "./pages/Training/Training";
import PaymentCallback from "./pages/PaymentCallback";
import PaymentHistory from "./pages/PaymentHistory";
import BillDetail from "./pages/BillDetail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import TenantNotFound from "./pages/TenantNotFound";
import PlatformBilling from "./pages/PlatformBilling/PlatformBilling";
import ReferralProgram from "./pages/ReferralProgram";
import PromoCampaignHost from "./components/Promo/PromoCampaignHost";

import PublicRoute from "./components/PublicRoute";
import {
  APP_SIDEBAR_PX,
  APP_SIDEBAR_STORAGE_KEY,
} from "./components/layoutConstants";

function readSidebarOpen() {
  try {
    const v = localStorage.getItem(APP_SIDEBAR_STORAGE_KEY);
    // Default open when unset
    if (v === null) return true;
    return v === "1";
  } catch {
    return true;
  }
}

function MainShell() {
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarOpen);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(APP_SIDEBAR_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <Navbar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="relative flex flex-1 min-h-0 overflow-hidden">
        <Sidebar sidebarOpen={sidebarOpen} />
        <main
          className="flex-1 min-w-0 p-4 sm:p-5 overflow-auto bg-slate-50 transition-[margin] duration-200 ease-out"
          style={{ marginLeft: sidebarOpen ? APP_SIDEBAR_PX : 0 }}
        >
          <Outlet />
        </main>
      </div>
      <PromoCampaignHost />
    </div>
  );
}

function PrivateMainLayout() {
  return (
    <PrivateRoute>
      <MainShell />
    </PrivateRoute>
  );
}

/** Specific /applications/* routes must be registered before applications/:positionId */
const privateMainChildren = [
  {
    index: true,
    element: (
      <RoleBasedRoute path="/">
        <Dashboard />
      </RoleBasedRoute>
    ),
  },
  {
    path: "employees",
    element: (
      <RoleBasedRoute path="/employees">
        <Employees />
      </RoleBasedRoute>
    ),
  },
  { path: "employees/add", element: <AddEmployeeModal /> },
  { path: "employees/update/:id", element: <AddEmployeeModal /> },
  {
    path: "employees/:id/documents/:credential",
    element: <ViewDocuments />,
  },
  { path: "upload-documents", element: <UploadDocuments /> },
  { path: "upload-documents/:id", element: <UploadDocuments /> },
  { path: "credentials", element: <Credentials /> },
  { path: "credentials/expired", element: <ExpiredCredentials /> },
  { path: "credentials/missing", element: <MissingCredentials /> },
  {
    path: "evaluation/*",
    element: (
      <RoleBasedRoute path="/evaluation">
        <EvaluationNew />
      </RoleBasedRoute>
    ),
  },
  { path: "applications/tb-test", element: <TbTestForm /> },
  {
    path: "applications/health-examination",
    element: <EmployeeHealthExamination />,
  },
  { path: "applications/flu-vaccination", element: <FluVaccinationForm /> },
  { path: "applications/route-sheet", element: <Navigate to="/general-forms" replace /> },
  { path: "applications/patient-consent", element: <PatientConsentForm /> },
  {
    path: "applications/medicare-non-coverage",
    element: <MedicareNonCoverageForm />,
  },
  { path: "applications/rn-welcome-letter", element: <RnWelcomeLetter /> },
  { path: "submitted-documents", element: <SubmittedDocuments /> },
  {
    path: "invoice",
    element: (
      <RoleBasedRoute path="/invoice">
        <Invoice />
      </RoleBasedRoute>
    ),
  },
  {
    path: "applications",
    element: (
      <RoleBasedRoute path="/applications">
        <ApplicationsRedirect />
      </RoleBasedRoute>
    ),
  },
  {
    path: "applications/:positionId",
    element: (
      <RoleBasedRoute path="/applications">
        <ApplicationForm />
      </RoleBasedRoute>
    ),
  },
  {
    path: "patients",
    element: (
      <RoleBasedRoute path="/patients">
        <Patients />
      </RoleBasedRoute>
    ),
  },
  {
    path: "patients/add",
    element: (
      <RoleBasedRoute path="/patients">
        <PatientForm />
      </RoleBasedRoute>
    ),
  },
  {
    path: "patients/:id",
    element: (
      <RoleBasedRoute path="/patients">
        <Navigate to="charts" replace />
      </RoleBasedRoute>
    ),
  },
  {
    path: "patients/:id/charts",
    element: (
      <RoleBasedRoute path="/patients">
        <PatientChartsPage />
      </RoleBasedRoute>
    ),
  },
  {
    path: "patients/:id/charts/new",
    element: (
      <RoleBasedRoute path="/patients">
        <PatientChartFormPage />
      </RoleBasedRoute>
    ),
  },
  {
    path: "patients/:id/charts/:chartId/edit",
    element: (
      <RoleBasedRoute path="/patients">
        <PatientChartFormPage />
      </RoleBasedRoute>
    ),
  },
  {
    path: "patients/:id/charts/:chartId/episodes/:episodeId",
    element: (
      <RoleBasedRoute path="/patients">
        <PatientDetail />
      </RoleBasedRoute>
    ),
  },
  {
    path: "patients/:patientId/forms/:formId",
    element: (
      <RoleBasedRoute path="/patients">
        <PatientClinicalFormPage />
      </RoleBasedRoute>
    ),
  },
  {
    path: "general-forms",
    element: <Navigate to="/general-forms/list" replace />,
  },
  {
    path: "general-forms/library",
    element: (
      <RoleBasedRoute path="/general-forms">
        <GeneralForms />
      </RoleBasedRoute>
    ),
  },
  {
    path: "general-forms/list",
    element: (
      <RoleBasedRoute path="/general-forms">
        <GeneralFormsList />
      </RoleBasedRoute>
    ),
  },
  {
    path: "coverage-area",
    element: (
      <RoleBasedRoute path="/coverage-area">
        <CoverageArea />
      </RoleBasedRoute>
    ),
  },
  {
    path: "surveyor",
    element: (
      <RoleBasedRoute path="/surveyor">
        <SurveyorAssignment />
      </RoleBasedRoute>
    ),
  },
  {
    path: "documents",
    element: (
      <RoleBasedRoute path="/documents">
        <Documents />
      </RoleBasedRoute>
    ),
  },
  { path: "documents/:id", element: <Documents /> },
  {
    path: "reports",
    element: (
      <RoleBasedRoute path="/reports">
        <Reports />
      </RoleBasedRoute>
    ),
  },
  {
    path: "payroll",
    element: (
      <RoleBasedRoute path="/payroll">
        <Payroll />
      </RoleBasedRoute>
    ),
  },
  {
    path: "qa-center",
    element: (
      <RoleBasedRoute path="/qa-center">
        <QaCenter />
      </RoleBasedRoute>
    ),
  },
  {
    path: "billing",
    element: (
      <RoleBasedRoute path="/billing">
        <BillingModule />
      </RoleBasedRoute>
    ),
  },
  {
    path: "billing/claims/:claimId",
    element: (
      <RoleBasedRoute path="/billing">
        <Navigate to="/billing" replace />
      </RoleBasedRoute>
    ),
  },
  {
    path: "libraries",
    element: (
      <RoleBasedRoute path="/libraries">
        <LibrariesModule />
      </RoleBasedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="agencies" replace /> },
      {
        path: "care-plan-templates",
        element: <CarePlanLibraryHub />,
      },
      {
        path: "care-plan-templates/:sectionKey",
        element: <CarePlanSectionPage />,
      },
      {
        path: ":sectionId",
        element: <LibrariesSectionPage />,
      },
    ],
  },
  { path: "manage-subscriptions", element: <ManageSubscriptions /> },
  {
    path: "platform-billing",
    element: (
      <RoleBasedRoute path="/platform-billing">
        <PlatformBilling />
      </RoleBasedRoute>
    ),
  },
  {
    path: "manage-subscriptions/payment-history",
    element: <PaymentHistory />,
  },
  { path: "payment/callback", element: <PaymentCallback /> },
  {
    path: "bills",
    element: <Navigate to="/manage-subscriptions" replace />,
  },
  { path: "bills/:id", element: <BillDetail /> },
  { path: "change-password", element: <ChangePassword /> },
  { path: "profile", element: <Profile /> },
  { path: "referral", element: <ReferralProgram /> },
  {
    path: "training",
    element: (
      <RoleBasedRoute path="/training">
        <Training />
      </RoleBasedRoute>
    ),
  },
  {
    path: "support",
    element: (
      <RoleBasedRoute path="/support">
        <Support />
      </RoleBasedRoute>
    ),
  },
];

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <PublicRoute>
        <Login />
      </PublicRoute>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <PublicRoute>
        <ForgotPassword />
      </PublicRoute>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <PublicRoute>
        <ResetPassword />
      </PublicRoute>
    ),
  },
  {
    path: "/tenant-not-found",
    element: <TenantNotFound />,
  },
  {
    path: "/general-forms/daily-log",
    element: (
      <PrivateRoute>
        <div className="bg-gray-50 min-h-screen">
          <DailyLog />
        </div>
      </PrivateRoute>
    ),
  },
  {
    path: "/general-forms/daily-log/:id",
    element: (
      <PrivateRoute>
        <div className="bg-gray-50 min-h-screen">
          <DailyLog />
        </div>
      </PrivateRoute>
    ),
  },
  {
    path: "/general-forms/route-sheet",
    element: (
      <PrivateRoute>
        <RequireVisibleGeneralForm formId="route-sheet">
          <div className="bg-gray-50 min-h-screen">
            <RouteSheet />
          </div>
        </RequireVisibleGeneralForm>
      </PrivateRoute>
    ),
  },
  {
    path: "/general-forms/route-sheet/:id",
    element: (
      <PrivateRoute>
        <RequireVisibleGeneralForm formId="route-sheet">
          <div className="bg-gray-50 min-h-screen">
            <RouteSheet />
          </div>
        </RequireVisibleGeneralForm>
      </PrivateRoute>
    ),
  },
  {
    path: "/general-forms/patient-consent",
    element: (
      <PrivateRoute>
        <div className="bg-gray-50 min-h-screen">
          <PatientConsent />
        </div>
      </PrivateRoute>
    ),
  },
  {
    path: "/general-forms/patient-consent/:id",
    element: (
      <PrivateRoute>
        <div className="bg-gray-50 min-h-screen">
          <PatientConsent />
        </div>
      </PrivateRoute>
    ),
  },
  {
    path: "/general-forms/medicare-non-coverage",
    element: (
      <PrivateRoute>
        <div className="bg-gray-50 min-h-screen">
          <MedicareNonCoverage />
        </div>
      </PrivateRoute>
    ),
  },
  {
    path: "/general-forms/medicare-non-coverage/:id",
    element: (
      <PrivateRoute>
        <div className="bg-gray-50 min-h-screen">
          <MedicareNonCoverage />
        </div>
      </PrivateRoute>
    ),
  },
  {
    // Documents bulk / multi-form print preview (PrintShell — same as Patient).
    path: "/documents/print-preview",
    element: (
      <PrivateRoute>
        <RoleBasedRoute path="/documents">
          <DocumentsPrintPreviewPage />
        </RoleBasedRoute>
      </PrivateRoute>
    ),
  },
  {
    // Bulk print preview — must be registered before the single-form print route.
    path: "/patients/:patientId/forms/bulk-print",
    element: (
      <PrivateRoute>
        <RoleBasedRoute path="/patients">
          <PatientClinicalFormsBulkPrintPage />
        </RoleBasedRoute>
      </PrivateRoute>
    ),
  },
  {
    // Opens in its own tab, so it deliberately skips the sidebar and navbar.
    path: "/patients/:patientId/forms/:formId/print",
    element: (
      <PrivateRoute>
        <RoleBasedRoute path="/patients">
          <PatientClinicalFormPrintPage />
        </RoleBasedRoute>
      </PrivateRoute>
    ),
  },
  {
    // Demographics print preview — screen toolbar only; paper is info-only.
    path: "/patients/:patientId/demographics/print",
    element: (
      <PrivateRoute>
        <RoleBasedRoute path="/patients">
          <PatientDemographicsPrintPage />
        </RoleBasedRoute>
      </PrivateRoute>
    ),
  },
  {
    path: "/patients/:patientId/assessment/print",
    element: (
      <PrivateRoute>
        <RoleBasedRoute path="/patients">
          <PatientAssessmentPrintPage />
        </RoleBasedRoute>
      </PrivateRoute>
    ),
  },
  {
    path: "/patients/:patientId/staff-schedule/print",
    element: (
      <PrivateRoute>
        <RoleBasedRoute path="/patients">
          <PatientStaffSchedulePrintPage />
        </RoleBasedRoute>
      </PrivateRoute>
    ),
  },
  {
    path: "/",
    element: <PrivateMainLayout />,
    children: privateMainChildren,
  },
]);

function App() {
  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        containerStyle={{ zIndex: 9999 }}
      />
      <RouterProvider router={router} />
    </>
  );
}

export default App;
